import React, { useEffect, useRef, useState } from 'react'
import Breadcrumbs from '../../components/breadcrumbs/Breadcrumbs'
import { useParams, useNavigate } from "react-router";
import AddModel from '../../components/Add/AddModel';
import PurchaseTypeDropdown from './components/PurchaseTypeDropdown';
import AddBottomButton from '../../components/Add/AddBottomButton';
import { RootState } from '../../store';
import { useSelector, useDispatch } from 'react-redux';
import useApi from '../../services/useApi';
import { setDestinationField, setMode, setSourceField } from '../../store/slices/addManualSlice';
import { showToast } from "../../components/toastMessage/toastSlice";
import useTranslation from "../../components/CustomHooks/useTranslation";
import { inputFieldContent } from "../../components/inputfields/InputFieldContent";
import { convertDate } from '../../components/commonfunctions/convertDate';
import moment from 'moment';


export default function PayDueBill() {
    const { id, dataId } = useParams();
    const { getAPI, PatchAPI } = useApi();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const sourceRefs = useRef<(HTMLDivElement | null)[]>([]);
    const { notEnglish } = useTranslation(inputFieldContent);
    const pathname = useSelector((state: RootState) => state.geolocation.previousUrl);
    const [loading, setLoading] = useState(false);
    const [remainingAmount, setRemainingAmount] = useState(0);
    const allDetails = useSelector((state: any) => state.addManual || {});

    const [errors, setErrors] = useState<{
        purchase_details?: {
            source_accounts?: Array<{
                source_account_id?: string;
                source_account_name?: string;
                purchased_date?: string;
                purchased_amount?: string;
                receipt_url?: string;
                note?: string;
            }>;
            destination_accounts?: Array<{
                destination_account_id?: string;
                destination_account_name?: string;
                purchased_amount?: string;
                pending_amount?: string;
                receipt_url?: string;
                note?: string;
            }>;
        };
    }>({
        purchase_details: {
            source_accounts: [
                {
                    source_account_id: "",
                    source_account_name: "",
                    purchased_date: "",
                    purchased_amount: "",
                    receipt_url: "",
                    note: "",
                },
            ],
            destination_accounts: [
                {
                    destination_account_id: "",
                    destination_account_name: "",
                    purchased_amount: "",
                    pending_amount: "",
                    receipt_url: "",
                    note: "",
                },
            ],
        },
    });

    const fetchDestinationAccounts = async () => {
        try {
            const response = await getAPI(`manual-entries/purchases/${dataId}`);

            if (response.data && response.data.data) {
                const purchaseData = response.data.data;
                const formattedDate = moment(purchaseData?.purchased_date, 'DD-MM-YYYY').format('DD MMM YYYY');
                setRemainingAmount(purchaseData.pending_amount);
                // Set the transaction mode
                dispatch(setMode(purchaseData.purchase_type as "Paid" | "Advance"));

                // Update the first destination in the destinations array (index 0)
                dispatch(setDestinationField({
                    destination_account_id: 0, // Index of the first destination
                    id: "destination_account_id",
                    value: purchaseData.account_id
                }));

                dispatch(setDestinationField({
                    destination_account_id: 0,
                    id: "destination_account_name",
                    value: purchaseData.account_name
                }));
                dispatch(setDestinationField({
                    destination_account_id: 0,
                    id: "purchased_date",
                    value: formattedDate
                }));

                dispatch(setDestinationField({
                    destination_account_id: 0,
                    id: "purchased_amount",
                    value: purchaseData.purchased_amount
                }));

                dispatch(setDestinationField({
                    destination_account_id: 0,
                    id: "pending_amount",
                    value: purchaseData.pending_amount
                }));

                dispatch(setDestinationField({
                    destination_account_id: 0,
                    id: "receipt_url",
                    value: purchaseData.receipt_url
                }));        

                dispatch(setDestinationField({
                    destination_account_id: 0,
                    id: "notes",
                    value: purchaseData.note || ""
                }));
            }
            return response.data;
        } catch (error) {
            console.error("Error fetching destination accounts:", error);
            return [];
        }
    };

    useEffect(() => {
        fetchDestinationAccounts();
    }, []);

    const handleSubmit = async () => {
        // Validate fields
        const newErrors = {
            purchase_details: {
                source_accounts: allDetails.sources?.map((source: any) => {
                    if (!source.source_account_name) {
                        return { source_account_name: "This field is required" };
                    }
                    return {
                        source_account_name: !source.source_account_name ? "This field is required" : "",
                        purchased_amount: !source.purchased_amount ? "This field is required" : "",
                        purchased_date: !source.purchased_date ? "This field is required" : "",
                        receipt_url: !source.receipt_url ? "This field is required" : "",
                        note: ""
                    };
                }) || [],
                destination_accounts: allDetails.destinations?.map((destination: any) => {
                    if (!destination.destination_account_name) {
                        return { destination_account_name: "This field is required" };
                    }
                    return {
                        destination_account_name: !destination.destination_account_name ? "This field is required" : "",
                        purchased_amount: !destination.purchased_amount ? "This field is required" : "",
                        pending_amount: !destination.pending_amount ? "This field is required" : "",
                        receipt_url: !destination.receipt_url ? "This field is required" : "",
                        note: ""
                    };
                }) || []
            }
        };

        setErrors(newErrors);

        // Check if there are any errors
        const hasSourceErrors = newErrors.purchase_details.source_accounts.some(
            (error: any) => Object.values(error).some(value => value !== "")
        );

        const hasDestinationErrors = newErrors.purchase_details.destination_accounts.some(
            (error: any) => Object.values(error).some(value => value !== "")
        );

        if (hasSourceErrors || hasDestinationErrors) {
            // Find the first error index
            const errorIndex = newErrors.purchase_details.source_accounts.findIndex(
                (error: any) => Object.values(error).some(value => value !== "")
            );

            // Scroll to the error element using ref
            if (errorIndex !== -1 && sourceRefs.current[errorIndex]) {
                sourceRefs.current[errorIndex].scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
            return;
        }

        // Proceed with submission if no errors
        setLoading(true);

        // Build payload
        const payload = {
            type: "Pay Due Amount", // Always "Paid" for Pay Due Bill
            details: {
                source_accounts: allDetails.sources.map((source: {
                    source_account_id: number | string,
                    source_account_name: string,
                    purchased_amount: string | number,
                    purchased_date: string,
                    receipt_url?: string,
                    notes?: string
                }) => ({
                    source_account_id: source.source_account_id.toString(),
                    source_account_name: source.source_account_name,
                    purchased_amount: Number(source.purchased_amount),
                    purchased_date: source.purchased_date,
                    receipt_url: source.receipt_url,
                    ...(source.notes && { note: source.notes }),
                })),
                destination_accounts: allDetails.destinations.map((destination: {
                    destination_account_id: number | string,
                    destination_account_name: string,
                    pending_amount: string | number,
                    purchased_date: string,
                    receipt_url?: string,
                    notes?: string
                }) => ({
                    destination_account_id: destination.destination_account_id.toString(),
                    destination_account_name: destination.destination_account_name,
                    purchased_date: convertDate(destination.purchased_date),
                    purchased_amount: Number(destination.pending_amount),
                    receipt_url: destination.receipt_url,
                    ...(destination.notes && { note: destination.notes }),
                })),
                credit_status: true, // Always true for Pay Due Bill
            },
        };

        // Verify amounts
        const totalSourceAmount = allDetails.sources.reduce((total: number, source: any) => {
            const amount = parseFloat(source.purchased_amount) || 0;
            return total + amount;
        }, 0);

        const totalDestinationAmount = allDetails.destinations.reduce((total: number, destination: any) => {
            const amount = parseFloat(destination.pending_amount) || 0;
            return total + amount;
        }, 0);
        if (totalSourceAmount === 0 || totalDestinationAmount === 0 || totalSourceAmount && totalDestinationAmount ===0  ) {
            dispatch(
                showToast({
                    type: "error",
                    message: "Total Source Amount and Destination Amount must be greater than zero.",
                })
            );
            setLoading(false);
            return;
        }

        if (totalSourceAmount !== totalDestinationAmount) {
            dispatch(
                showToast({
                    type: "error",
                    message: "Total Source and Destination Amount must be equal.",
                })
            );
            setLoading(false);
            return;
        }

        if (remainingAmount < totalDestinationAmount) {
            dispatch(
                showToast({
                    type: "error",
                    message: "Destination Pending Amount exceeds the remaining balance due.",
                })
            );
            setLoading(false);
            return;
        }

        try {
            const response = await PatchAPI(`manual-entries/purchases/${dataId}`, payload);

            if (!response?.error) {
                dispatch(
                    showToast({
                        type: "success",
                        message: response?.data?.message || "Payment completed successfully",
                    })
                );
                navigate(`/mno-management/list-of-mnos/view-mno/${id}/purchased-amount-transaction?payment_status=false`);
            } else {
                dispatch(
                    showToast({
                        type: "error",
                        message: response?.data?.response?.data?.message || "Failed to pay bill.",
                    })
                );
            }
        } catch (error) {
            console.error("Error paying bill:", error);
            dispatch(
                showToast({
                    type: "error",
                    message: "An unexpected error occurred. Please try again.",
                })
            );
        } finally {
            setLoading(false);
        }
    };

    return (
        <div>
            <Breadcrumbs
                navPath={["MNO Management", "List of MNO's", "View Details", "List of Purchased Amount Transaction", "Pay Due Bill"]}
                heading="Pay Due Bill"
                noNavigation
                links={["/mno-management/list-of-mnos", "/mno-management/list-of-mnos", `/mno-management/list-of-mnos/view-mno/${id}`, pathname ? pathname : `/mno-management/list-of-mnos/view-mno/${id}/purchased-amount-transaction`]}
                hasAddPrivilege={true}
            />
            <AddModel>
                <div className="flex flex-col" dir={notEnglish ? 'rtl' : 'ltr'}>
                    <PurchaseTypeDropdown
                        isSource={true}
                        singleDestination={true}
                        selectedOption={"Paid"}
                        setErrors={setErrors}
                        errors={errors}
                        sourceRefs={sourceRefs}
                    />
                    <PurchaseTypeDropdown
                        isSource={false}
                        selectedOption={"Paid"}
                        disabled={true}
                        setErrors={setErrors}
                        errors={errors}
                        loading={loading}
                        payDueBill={true}
                    />
                </div>
            </AddModel>
            <AddBottomButton
                buttonName='Confirm'
                loading={loading}
                onClick={handleSubmit}
                buttonWidth={'w-[140px]'}
                buttonClass={`w-[140px] whitespace-nowrap`}
            />
        </div>
    )
}